import React from 'react';
import { Link as ScrollLink } from 'react-scroll/modules';
import { t } from '../../utils/i18n';
import {
  baseXUnit,
  breakpoints,
  color,
  font,
  fontSize,
  linkStyle
} from '../../styles/theme';
import Icon from '../common/Icon';
import EllipsisOverflow from '../common/EllipsisOverflow';
import IconText from '../common/IconText';
import DivButton from '../a11y/DivButton';
import { DEFAULT_NAVBAR_ANCHOR_OFFSET_IN_PIXELS } from '../navigation/DefaultNavbar';
import { MediaGalleryItemPhase } from './MediaGalleryModal';

export const mediaGalleryModalHeaderDesktopHeightBase = 12;

export interface MediaGalleryModalHeaderProps {
  phase?: MediaGalleryItemPhase;
  closeDialog: () => void;
  currentSlideNumber: number;
  totalSlideNumber: number;
}

const MediaGalleryModalHeader: React.FunctionComponent<
  MediaGalleryModalHeaderProps
> = ({ closeDialog, currentSlideNumber, totalSlideNumber, phase }) => {
  return (
    <div
      className="media-gallery-modal-header"
      data-testid="media-gallery-modal-header"
    >
      <div className="close-button-wrapper">
        <DivButton onClick={closeDialog} data-cy="close-media-gallery-modal">
          <Icon
            name="nav-close"
            color="white"
            size={[4, 5, 5]}
            screenReaderLabel={t('Sulje näkymä')}
          />
        </DivButton>
      </div>
      <div className="top">
        <div className="pager">
          {totalSlideNumber > 1 && (
            <span data-testid="current-slide">
              {currentSlideNumber} / {totalSlideNumber}
            </span>
          )}
        </div>
      </div>
      {phase && (
        <div className="bottom">
          <ScrollLink
            to={phase.scrollTarget}
            offset={DEFAULT_NAVBAR_ANCHOR_OFFSET_IN_PIXELS}
            onClick={() => closeDialog()}
            smooth
            className="bottom-link"
          >
            <IconText
              iconSize={[2, 4, 4]}
              iconColor="white"
              icon={phase.iconName}
            >
              <EllipsisOverflow>{phase && phase.title}</EllipsisOverflow>
              <div className="phase-arrow">
                <Icon name="nav-arrow-right" color="white" size={[2, 4, 4]} />
              </div>
            </IconText>
          </ScrollLink>
        </div>
      )}
      {/*language=CSS*/}
      <style jsx>{`
        .media-gallery-modal-header {
          padding: ${baseXUnit(2)};
          padding-bottom: ${baseXUnit(5)};
          background-image: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.75),
            rgba(0, 0, 0, 0)
          );
          color: ${color.white};
          font-family: ${font.mono};
          position: relative;
        }

        .close-button-wrapper {
          position: absolute;
          top: ${baseXUnit(2)};
          right: ${baseXUnit(2)};
        }

        .top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: ${fontSize.px11};
          height: ${baseXUnit(4)};
        }

        .bottom {
          /* Cap the width to prevent button clashes */
          max-width: 66%;
          font-size: ${fontSize.px14};
        }

        :global(.bottom-link) {
          display: inline-block;
          max-width: 100%;
          cursor: pointer;
          ${linkStyle.withAnimation}
        }

        :global(.bottom-link:hover) {
          ${linkStyle.hover}
        }

        :global(.phase-arrow) {
          opacity: 0.7;
        }

        @media ${breakpoints.medium} {
          .media-gallery-modal-header {
            padding-left: ${baseXUnit(3)};
            padding-bottom: 0;
            height: ${baseXUnit(mediaGalleryModalHeaderDesktopHeightBase)};
          }

          .top {
            align-items: flex-start;
            height: ${baseXUnit(2.5)};
          }

          .bottom {
            font-size: ${fontSize.px18};
            line-height: ${baseXUnit(3)};
          }
        }
      `}</style>
    </div>
  );
};

export default MediaGalleryModalHeader;
